import React from 'react';
import { cn } from '@/utils';

export interface SkeletonLoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The height of the skeleton loader.
   * 
   * - `xs`: 1rem (16px)
   * - `sm`: 1.5rem (24px)
   * - `md`: 2rem (32px)
   * - `lg`: 3rem (48px)
   * - `xl`: 4rem (64px)
   * - `2xl`: 6rem (96px)
   * - `3xl`: 10rem (160px)
   * - `full`: 100% of the parent container's height
   */
  height?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';

  /**
   * The width of the skeleton loader.
   * 
   * - `xs`: 4rem (64px)
   * - `sm`: 6rem (96px)
   * - `md`: 8rem (128px)
   * - `lg`: 12rem (192px)
   * - `xl`: 16rem (256px)
   * - `2xl`: 20rem (320px)
   * - `3xl`: 24rem (384px)
   * - `full`: 100% of the parent container's width
   * - `1/2`: 50% of the parent container's width
   * - `1/3`: 33.333% of the parent container's width
   * - `2/3`: 66.666% of the parent container's width
   * - `1/4`: 25% of the parent container's width
   * - `3/4`: 75% of the parent container's width
   */
  width?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | 'full'
    | '1/2'
    | '1/3'
    | '2/3'
    | '1/4'
    | '3/4';

  /**
   * The border radius of the skeleton loader.
   * 
   * - `none`: No border radius
   * - `sm`: 0.125rem (2px)
   * - `md`: 0.25rem (4px)
   * - `lg`: 0.5rem (8px)
   * - `xl`: 1rem (16px)
   * - `2xl`: 2rem (32px)
   * - `3xl`: 4rem (64px)
   * - `full`: 9999px (fully rounded)
   */
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';

  /**
   * Whether to animate the skeleton loader with a pulse effect.
   */
  animated?: boolean;

  /**
   * The background color variant of the skeleton loader.
   * 
   * - `light`: bg-neutral-gray-100
   * - `medium`: bg-neutral-gray-200
   * - `dark`: bg-neutral-gray-300
   */
  variant?: 'light' | 'medium' | 'dark';

  /**
   * The HTML element type to render the skeleton loader as. Defaults to a `div`.
   * This allows the skeleton loader to be rendered as a different semantic 
   * element if needed (e.g., `span`, `li`, etc.). 
   */
  asTag?: React.ElementType;
}

/**
 * Height classes mapping
 */
const heightClasses = {
  xs: 'h-4',
  sm: 'h-6',
  md: 'h-8',
  lg: 'h-12',
  xl: 'h-16',
  '2xl': 'h-24',
  '3xl': 'h-40',
  full: 'h-full',
} as const;

/**
 * Width classes mapping
 */
const widthClasses = {
  xs: 'w-16',
  sm: 'w-24',
  md: 'w-32',
  lg: 'w-48',
  xl: 'w-64',
  '2xl': 'w-80',
  '3xl': 'w-96',
  full: 'w-full',
  '1/2': 'w-1/2',
  '1/3': 'w-1/3',
  '2/3': 'w-2/3',
  '1/4': 'w-1/4',
  '3/4': 'w-3/4',
} as const;

/**
 * Rounded classes mapping
 */
const roundedClasses = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  '3xl': 'rounded-3xl',
  full: 'rounded-full',
} as const;

/**
 * Variant classes mapping
 */
const variantClasses = {
  light: 'bg-neutral-gray-100',
  medium: 'bg-neutral-gray-200',
  dark: 'bg-neutral-gray-300',
} as const;

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  height = '3xl',
  width = 'full',
  rounded = 'md',
  animated = true,
  variant = 'dark',
  className,
  asTag: Component = 'div',
  ...props
}) => {
  return (
    <Component
      className={cn(
        heightClasses[height],
        widthClasses[width],
        roundedClasses[rounded],
        variantClasses[variant],
        animated && 'animate-pulse',
        className,
      )}
      role="status"
      aria-label="Loading..."
      {...props}
    />
  );
};
